import { useState, forwardRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { ArrowUpRight, ArrowDownRight, Linkedin, Mail, MapPin, BookOpen } from "lucide-react";
import ExecutiveSummary from "@/components/ExecutiveSummary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Contact = forwardRef(function Contact(_props, ref) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", engagement_type: "Board / Independent Director", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (form.name.trim().length < 2 || !form.email || form.message.trim().length < 10) {
      toast.error("Please complete name, email and a brief message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/inquiries`, form);
      toast.success(res.data.message || "Thank you. Your inquiry has been received.");
      setForm({ name: "", email: "", organization: "", role: "", engagement_type: "Board / Independent Director", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again shortly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="relative border-b border-surface-2 overflow-hidden">
      {/* Static boardroom backdrop keeps scrolling responsive. */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0">
          <img
            src="/asad_seated_wide.jpg"
            alt=""
            width="1536"
            height="512"
            className="h-full w-full object-cover object-center"
            loading="lazy"
            decoding="async"
          />
        </div>
        {/* dark navy overlay for legibility */}
        <div className="absolute inset-0 bg-ink/85" />
        {/* left-to-right editorial gradient to weight the form side */}
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(10,22,40,0.95)_0%,rgba(10,22,40,0.7)_45%,rgba(10,22,40,0.55)_100%)]" />
        {/* subtle top/bottom vignette */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,22,40,0.6)_0%,transparent_20%,transparent_80%,rgba(10,22,40,0.6)_100%)]" />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="col-span-12 md:col-span-5"
          >
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">
              <span className="tabular">13 —</span><span className="ml-3">Get in Touch</span>
            </div>
            <h2 className="mt-4 font-serif-display font-light text-ivory text-5xl md:text-6xl leading-[0.95] tracking-[-0.02em]">
              For chairs, search firms<br />
              <span className="italic text-brass">and expert-network clients.</span>
            </h2>
            <p className="mt-8 text-mutedfg leading-relaxed font-light max-w-md">
              Board mandates, expert calls or advisory projects — please share a brief note. Enquiries are
              read personally and answered within two business days.
            </p>

            <div className="mt-10 space-y-3 text-ivory/85 text-sm">
              <div className="flex items-center gap-3">
                <Mail size={14} className="text-brass" />
                <a
                  href="mailto:asadhatimi@gmail.com"
                  className="link-underline hover:text-brass"
                >
                  asadhatimi@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Linkedin size={14} className="text-brass" />
                <a
                  href="https://www.linkedin.com/in/asad-hatimi-program-leader"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline hover:text-brass"
                >
                  linkedin.com/in/asad-hatimi-program-leader
                </a>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-brass" />
                <span>Udaipur, Rajasthan · India</span>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <a
                href="/Asad_Hatimi_CV.pdf"
                download
                data-testid={CONTACT.cvDownload}
                className="group inline-flex items-center gap-3 border border-brass px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-brass hover:bg-brass hover:text-ink transition-colors duration-500"
              >
                Download Curriculum
                <ArrowDownRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" />
              </a>
              <ExecutiveSummary>
                <button
                  type="button"
                  data-testid={CONTACT.execSummary}
                  className="group inline-flex items-center gap-3 bg-brass px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-ink font-semibold hover:-translate-y-[2px] transition-transform duration-500"
                >
                  <BookOpen size={14} /> Executive Summary
                </button>
              </ExecutiveSummary>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid={CONTACT.form}
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={1}
            className="col-span-12 md:col-span-7 md:col-start-6 lg:col-start-7 border border-surface-2 p-8 md:p-12 bg-[#0d1b30]"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Name" testid={CONTACT.name} value={form.name} onChange={update("name")} required />
              <Field label="Email" testid={CONTACT.email} value={form.email} onChange={update("email")} type="email" required />
              <Field label="Organisation" testid={CONTACT.org} value={form.organization} onChange={update("organization")} />
              <Field label="Your Role" testid={CONTACT.role} value={form.role} onChange={update("role")} />
            </div>

            <div className="mt-6">
              <label className="block text-[11px] uppercase tracking-[0.22em] text-brass mb-3">
                Interested in
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  "Board / Independent Director",
                  "Expert Consulting (GLG · Guidepoint)",
                  "Advisory Project",
                ].map((opt) => (
                  <label
                    key={opt}
                    className={`cursor-pointer border px-4 py-3 text-xs uppercase tracking-[0.18em] transition-colors duration-500 ${
                      form.engagement_type === opt
                        ? "border-brass text-brass"
                        : "border-surface-2 text-ivory/80 hover:border-brass/60 hover:text-brass"
                    }`}
                  >
                    <input
                      type="radio"
                      name="engagement_type"
                      value={opt}
                      checked={form.engagement_type === opt}
                      onChange={update("engagement_type")}
                      className="sr-only"
                      data-testid={`engagement-radio-${opt.split(" ")[0].toLowerCase()}`}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-6">
              <label className="block text-[11px] uppercase tracking-[0.22em] text-brass mb-3">Message</label>
              <textarea
                data-testid={CONTACT.message}
                value={form.message}
                onChange={update("message")}
                rows={6}
                required
                minLength={10}
                className="w-full bg-transparent border border-surface-2 focus:border-brass outline-none px-4 py-3 text-ivory placeholder:text-mutedfg/60 font-light transition-colors duration-500"
                placeholder="A brief note on the mandate — sector, company, timing…"
              />
            </div>
            <div className="mt-8 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.22em] text-mutedfg">
                Replies within 2 business days
              </span>
              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-brass px-8 py-4 text-ink text-[13px] uppercase tracking-[0.2em] font-semibold disabled:opacity-50 transition-transform duration-500 hover:-translate-y-[2px]"
              >
                {loading ? "Sending…" : "Submit Inquiry"}
                <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
});

function Field({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.22em] text-brass mb-3">{label}</label>
      <input
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border border-surface-2 focus:border-brass outline-none px-4 py-3 text-ivory placeholder:text-mutedfg/60 font-light transition-colors duration-500"
      />
    </div>
  );
}

export default Contact;
